import { atom, useRecoilState } from 'recoil';

const chunksAtom = atom({
  key: 'chunksList',
  default: [
    {'name': 'Drive to Store', 'duration': {'minutes': 17}},
    {'name': 'Shop', 'duration': {'minutes': 26}},
    {'name': 'Drive to Restaurant', 'duration': {'minutes': 19}},
    {'name': 'Pickup Food', 'duration': {'minutes': 7 }},
    {'name': 'Drive to Home', 'duration': {'minutes': 16 }},
  ],
});

const fixedPointAtom = atom({
  key: 'fixedPoint',
  default: {
    minutesAfterMidnight: 570, // 1pm
    position: 'end' // start | end
  }
})

const dragAtom = atom({
  key: 'dragItem',
  default: null,
})

const hideBetweenButtonsAtom = atom({
  key: 'hideBetweenButtons',
  default: true,
})

function minutesToFormatted(minutesSinceMidnight) {
  let hours = Math.floor(minutesSinceMidnight / 60)
  let minutes = minutesSinceMidnight % 60

  hours = `${hours}`.padStart(2, '0')
  minutes = `${minutes}`.padStart(2, '0')

  return `${hours}:${minutes}`
}

function App() {
  const [fixed] = useRecoilState(fixedPointAtom)
  const [list, setList] = useRecoilState(chunksAtom);
  const [hideBetweenButtons] = useRecoilState(hideBetweenButtonsAtom)

  let startTime, endTime;
  switch (fixed.position) {
    case 'start':
      startTime = fixed.minutesAfterMidnight
      endTime = startTime + list.map((e) => e.duration.minutes).reduce((e, a) => e + a, 0)
      break;
    case 'end':
      endTime = fixed.minutesAfterMidnight
      startTime = endTime - list.map((e) => e.duration.minutes).reduce((e, a) => e + a, 0)
      break;
    default:
      break;
  }

  const formattedStart = minutesToFormatted(startTime)
  const formattedEnd = minutesToFormatted(endTime)

  return (
    <div key='app:a' style={{display: 'inline-block', fontFamily: 'ui-sans-serif', margin: '10px' }}>
      <div key='app:b' style={{display: 'flex', flexDirection: 'column'}}>
        <ChunkRow title='Start' key='startchunk'>
          <span style={{fontFamily: 'ui-monospace'}}>{formattedStart}</span>
        </ChunkRow>


        <div
          style={{
            borderRadius: '1rem',
            backgroundColor: 'rgba(255,255,255, 0.8)',
            width: '2rem',
            height: hideBetweenButtons ? '1rem' : '2rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            margin: '0.25rem 2rem',
            opacity: hideBetweenButtons ? 0 : 1,
          }}

          key={'button:start'}

          onClick={() => {
            const newList = [...list]
            newList.splice(0, 0, {'name': 'New Chunk', 'duration': {'minutes': 5}})
            setList(newList)
          }}
        >
          ＋
        </div>

        {
          list.map((chunk, index) => {
            let chunkStartsAtMinutesSinceMidnight = list.filter((e, i) => i <= index).map((e) => e.duration.minutes).reduce((e, a) => e + a, 0)
            return (
              <>
              <ChunkRow key={chunk.name + ':' + index} chunk={chunk} index={index} onRename={(newName) => {
                const newList = [...list]
                newList.splice(index, 1, {...chunk, 'name': newName})
                setList(newList)
              }}>
                <span style={{fontFamily: 'ui-monospace', whiteSpace: 'nowrap'}} contentEditable='true' suppressContentEditableWarning={true} onKeyDown={(e) => {
                  if (e.keyCode === 13) { e.target.blur() }
                }} onClick={(e) => {
                  let range = new Range();
                  range.setStart(e.target, 0);
                  range.setEnd(e.target, 1);

                  document.getSelection().addRange(range);
                } } onBlur={(e) => {
                  let parsed = parseInt(e.target.textContent, 10)
                  if (isNaN(parsed)) {
                    e.target.textContent = chunk.duration.minutes
                    return
                  }

                  const newList = [...list]
                  newList.splice(index, 1, {...chunk, 'duration': {'minutes': parsed}})
                  setList(newList)
          }}>{chunk.duration.minutes}</span><span onClick={(e) => {
            let target = e.target.previousSibling
            target.focus()

            let range = new Range();
            range.setStart(target, 0);
            range.setEnd(target, 1);

            document.getSelection().addRange(range);
          }}> min</span>
              </ChunkRow>
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                <div
                key={'button:' + index}
                style={{
                  borderRadius: '1rem',
                  backgroundColor: 'rgba(255,255,255, 0.8)',
                  width: '2rem',
                  height: hideBetweenButtons ? '1rem' : '2rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  margin: '0.25rem 2rem',
                  opacity: hideBetweenButtons ? 0 : 1,
                }}

                onClick={() => {
                  const newList = [...list]
                  newList.splice(index + 1, 0, {'name': 'New Chunk', 'duration': {'minutes': 5}})
                  setList(newList)
                }}
              >
                ＋
              </div>
              { (index !== (list.length - 1)) &&
                (<div style={{fontFamily: 'ui-monospace', whiteSpace: 'nowrap', marginRight: '11.5rem'}}>{minutesToFormatted(chunkStartsAtMinutesSinceMidnight + startTime)}</div>)
              }
              </div>
              </>
            )
          })
        }

        <ChunkRow title='End' key='endchunk'>
          <span style={{fontFamily: 'ui-monospace'}}>{formattedEnd}</span>
        </ChunkRow>
      </div>
    </div>
  );
}

function ChunkRow({chunk, index, title, children, onRename}) {
  const name = chunk?.name ?? title

  const [dragItem, setDragItem] = useRecoilState(dragAtom);
  const [list, setList] = useRecoilState(chunksAtom);

  const handleDragStart = (index) => {
    setDragItem(index);
  };

  const handleDragEnter = (e, index) => {
    const newList = [...list];
    const item = newList[dragItem];
    newList.splice(dragItem, 1);
    newList.splice(index, 0, item);
    setDragItem(index);
    setList(newList);
  };

  const handleDragLeave = (e) => {
    //
  };

  const handleDrop = (e) => {
    setDragItem(null)
  };

  const upButton = (
    <MoveButton
      key={`${name}:${index}:up`}
      enabled={index !== 0 && index !== undefined}
      visible={index !== undefined && index !== dragItem}
      onClick={() => {
        const newList = [...list];
        const a = newList[index - 1]
        const b = newList[index]
        newList[index - 1] = b
        newList[index] = a
        setList(newList)
      }}>
        ⇧
    </MoveButton>
  )

  const downButton = (
    <MoveButton
      key={`${name}:${index}:down`}
      enabled={index !== (list.length - 1) && index !== undefined}
      visible={index !== undefined && index !== dragItem}
      onClick={() => {
        const newList = [...list];
        const a = newList[index + 1]
        const b = newList[index]
        newList[index + 1] = b
        newList[index] = a
        setList(newList)
      }}>
        ⇩
    </MoveButton>
  )

  const deleteButton = (
    <MoveButton
      key={`${name}:${index}:delete`}
      enabled={true}
      visible={index !== undefined && index !== dragItem}
      onClick={() => {
        const newList = [...list];
        newList.splice(index, 1)
        setList(newList)
      }}>
      ×
    </MoveButton>
  )

  const description = (
    <div
    key={`${name}:${index}:desc`}
      style={{
        cursor: (index === undefined ? 'default' : 'move'),
        borderRadius: '5px',
        backgroundColor: 'rgba(255,255,255, 0.8)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        margin: '5px',
        maxWidth: '100%',
        filter: 'opacity(100%)',
        flexGrow: '1',
        alignItems: 'center',
      }}>
        {
          (<div style={{
            fontSize: '1.27rem',
            whiteSpace: 'nowrap',
          }} contentEditable='true' suppressContentEditableWarning={true} onKeyDown={(e) => {
                  if (e.keyCode === 13) { e.target.blur() }
                }}
            onBlur={(e) => {
            onRename && onRename(e.target.textContent)
          }}>{name}</div>)
        }
        <div style={{display: 'inline-block', width: '2rem'}}></div>
        <div style={{fontSize: '1rem', whiteSpace: 'nowrap'}}>
          {children}
        </div>
      </div>
    )

  if (chunk && index !== undefined) {
    return (
      <div
        draggable="true"
        onDragStart={() => handleDragStart(index)}
        onDragEnter={(e) => handleDragEnter(e, index)}
        onDragLeave={(e) => handleDragLeave(e)}
        onDrop={(e) => handleDrop(e)}
        onDragOver={(e) => e.preventDefault()}

        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          KhtmlUserDrag: 'element',
          maxWidth: 'calc(100% - 1rem)',
          paddingLeft: '1rem',
        }}
      >
        {description}

        {upButton}
        {downButton}
        {deleteButton}
      </div>
      )
  } else {
    return (
      <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          KhtmlUserDrag: 'element',
          maxWidth: '100%',
        }}
      >
        {description}

        {upButton}
        {downButton}
        {deleteButton}
      </div>
    )
  }
}

function MoveButton({children, enabled, visible, onClick}) {
  let cursor, opacity

  if (visible) {
    if (enabled) {
      cursor = 'pointer'
      opacity = '100%'
    } else {
      cursor = 'not-allowed'
      opacity = '50%'
    }
  } else {
    cursor = 'default'
    opacity = '0%'
  }

  return (
    <div style={{
        borderRadius: '5px',
        backgroundColor: 'rgba(255,255,255, 0.8)',
        padding: '0.75rem 1rem',
        margin: '5px',
        cursor: cursor,
        opacity: opacity,
      }} onClick={() => { enabled && visible && onClick() }}>
      {children}
    </div>
    )
}

export default App;
